<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import { moneyShort, hashIndex, sizeRange, sizeUnitLabel } from './helpers';
import { STATUS_STYLE, HUES } from './constants';
import { apiErrorMessage } from '../../../api/realEstate/errors';
import RealEstateIcon from './RealEstateIcon.vue';
import ActiveSwitch from './components/ActiveSwitch.vue';

export default {
  name: 'ProjectCard',
  components: { RealEstateIcon, ActiveSwitch },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return { isTogglingActive: false };
  },
  computed: {
    rollup() {
      return this.project.rollup;
    },
    statusLabel() {
      return this.project.project_status_label || this.project.project_status;
    },
    statusClass() {
      return STATUS_STYLE[this.project.project_status] || '';
    },
    coverStyle() {
      // Only the hue is set here; .re-cover-gradient (below) draws the
      // gradient.
      if (this.project.cover_image_url) return {};
      return {
        '--re-hue': HUES[hashIndex(this.project.property_id, HUES.length)],
      };
    },
    fromPriceLabel() {
      if (this.rollup.mixed_currency || this.rollup.price_min == null)
        return '';
      return this.$t('REAL_ESTATE.CARD.FROM_PRICE', {
        price: moneyShort(this.rollup.price_min, this.rollup.currency),
      });
    },
    locationText() {
      return [this.project.area, this.project.region]
        .filter(Boolean)
        .join(' · ');
    },
    subtypeLabel() {
      return this.project.property_subtype || this.project.property_type;
    },
    sizeRangeLabel() {
      const range = sizeRange(this.rollup);
      if (range == null) return null;
      return this.$t('REAL_ESTATE.CARD.SIZE_RANGE', {
        range,
        unit: sizeUnitLabel(this, this.rollup.size_unit),
      });
    },
  },
  methods: {
    ...mapActions('realEstate', ['setActive']),
    handleOpen() {
      this.$router.push({
        name: 'real_estate_project',
        params: {
          accountId: this.$route.params.accountId,
          projectId: this.project.property_id,
        },
      });
    },
    async handleToggleActive() {
      this.isTogglingActive = true;
      const isActive = !this.project.is_active;
      try {
        await this.setActive({
          propertyId: this.project.property_id,
          isActive,
        });
        useAlert(
          this.$t(
            isActive
              ? 'REAL_ESTATE.CARD.SUCCESS_ACTIVE'
              : 'REAL_ESTATE.CARD.SUCCESS_INACTIVE',
            { name: this.project.name }
          )
        );
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isTogglingActive = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="cursor-pointer relative bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-lg hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200 overflow-hidden"
    :class="{ 'opacity-70': !project.is_active }"
    @click="handleOpen"
  >
    <div
      class="relative h-40"
      :class="{ 're-cover-gradient': !project.cover_image_url }"
      :style="coverStyle"
    >
      <img
        v-if="project.cover_image_url"
        :src="project.cover_image_url"
        :alt="project.name"
        class="absolute inset-0 w-full h-full object-cover"
        @error="$event.target.remove()"
      />
      <div
        v-if="!project.cover_image_url"
        class="absolute inset-0 flex items-center justify-center text-slate-500/40"
      >
        <fluent-icon icon="condo" size="40" />
      </div>
      <div class="absolute top-3 left-3 flex items-center gap-1.5">
        <span
          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
          :class="statusClass"
        >
          {{ statusLabel }}
        </span>
        <span
          v-if="project.marketing_badge"
          class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300"
        >
          {{ project.marketing_badge }}
        </span>
      </div>
      <span
        class="absolute top-3 right-3 inline-flex px-1.5 py-1 rounded-full bg-white/90 dark:bg-slate-900/80 shadow"
      >
        <ActiveSwitch
          :value="project.is_active"
          :disabled="isTogglingActive"
          @toggle="handleToggleActive"
        />
      </span>
      <span
        v-if="project.buyer_restriction"
        class="absolute bottom-3 left-3 bg-slate-900/70 text-white px-2 py-1 rounded-md text-xs"
      >
        {{ project.buyer_restriction }}
      </span>
      <span
        v-if="project.tenure"
        class="absolute bottom-3 right-3 bg-slate-900/70 text-white px-2 py-1 rounded-md text-xs font-medium"
      >
        {{ project.tenure }}
      </span>
    </div>
    <div class="p-5" :class="{ 'opacity-60': !project.is_active }">
      <h3
        class="text-base font-semibold text-slate-900 dark:text-slate-100 truncate"
      >
        {{ project.name }}
      </h3>
      <p
        v-if="locationText"
        class="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 truncate mt-0.5"
      >
        <fluent-icon icon="location" size="13" />
        {{ locationText }}
      </p>
      <p class="text-lg font-bold text-woot-600 dark:text-woot-400 mt-3">
        <span
          v-if="rollup.mixed_currency"
          class="text-sm font-medium text-amber-600 dark:text-amber-400"
        >
          {{ $t('REAL_ESTATE.CARD.MIXED_CURRENCY') }}
        </span>
        <template v-else-if="rollup.price_min == null">
          {{ $t('REAL_ESTATE.CARD.NO_FLOOR_PLANS') }}
        </template>
        <template v-else>{{ fromPriceLabel }}</template>
      </p>
      <div
        class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-slate-500 dark:text-slate-400 text-xs"
      >
        <span>{{ subtypeLabel }}</span>
        <span v-if="sizeRangeLabel" class="flex items-center gap-1">
          <RealEstateIcon icon="ruler" :size="13" /> {{ sizeRangeLabel }}
        </span>
        <span v-if="rollup.max_bedrooms" class="flex items-center gap-1">
          <RealEstateIcon icon="bed" :size="13" />
          {{ $t('REAL_ESTATE.CARD.UP_TO_BEDS', { n: rollup.max_bedrooms }) }}
        </span>
      </div>
      <div
        class="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400"
      >
        <span class="flex items-center gap-1">
          <RealEstateIcon icon="layers" :size="13" />
          {{
            $tc('REAL_ESTATE.CARD.FLOOR_PLAN_COUNT', rollup.floor_plan_count, {
              count: rollup.floor_plan_count,
            })
          }}
        </span>
        <span>{{
          project.developer || $t('REAL_ESTATE.CARD.NO_DEVELOPER')
        }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Placeholder cover when a project has no image — the same pastel as the
   prototype, in light and dark mode alike. */
.re-cover-gradient {
  background: linear-gradient(
    135deg,
    hsl(var(--re-hue) 62% 90%),
    hsl(calc(var(--re-hue) + 45) 60% 78%)
  );
}
</style>
